import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { TEXT_DIRECTION } from 'helpers/isRtlLanguage';
import PropTypes from 'prop-types';
import { CDN_TYPE_CMS, imageUrlCDN } from 'utils';

import { Slider } from '@atlas/components';
import { Logger } from '@atlas/core';
import { Label } from '@atlas/ui';

import { BASE_PATH } from 'common/constants';
import {
  getSmarticoTournaments,
  useSmartico,
} from 'components/ClientProfile/Achievements/Smartico';
import { calculateTimeLeft } from 'components/Tournament/Smartico/shares';
import TournamentsCard from 'components/Tournament/Smartico/TournamentsCard';
import { useIsRtl } from 'hooks/useIsRtl';

const LobbyTournamentsBlock = ({ blockTitleKey, imageUrl }) => {
  const [isShowImg, setIsShowImg] = useState(true);
  const handleError = () => setIsShowImg(false);

  const isRtl = useIsRtl();
  const dir = useMemo(() => (isRtl ? TEXT_DIRECTION.RTL : TEXT_DIRECTION.LTR), [isRtl]);

  const { push } = useHistory();
  const { isReadySmartico } = useSmartico();
  const [loading, setLoading] = useState(true);
  const [tournaments, setTournaments] = useState(null);
  const [timeLeftForTournaments, setTimeLeftForTournaments] = useState({});

  const tournamentsUpdates = useCallback(tournaments => {
    Logger.debug('Smartico.tournaments', tournaments);
    setTournaments(tournaments?.filter(t => t.is_upcoming || t.is_in_progress) || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (isReadySmartico) {
      getSmarticoTournaments({
        cb: tournamentsUpdates,
        tournamentsUpdates,
      });
    }
  }, [isReadySmartico, tournamentsUpdates]);

  useEffect(() => {
    const updateTournamentTimes = () => {
      const updatedTimeLeft = {};
      tournaments?.forEach(tournament => {
        const targetTime = tournament.is_in_progress
          ? new Date(tournament.end_time).getTime()
          : new Date(tournament.start_time).getTime();
        updatedTimeLeft[tournament.instance_id] = calculateTimeLeft(targetTime);
      });
      setTimeLeftForTournaments(updatedTimeLeft);
    };

    const interval = setInterval(updateTournamentTimes, 1000);

    return () => clearInterval(interval);
  }, [tournaments]);

  if (loading || !tournaments || tournaments.length === 0) {
    return null;
  }

  return (
    <div className="casino-lobby__item lobby-tournaments">
      <div className="casino-lobby__header">
        <div className="casino-lobby__left">
          {isShowImg && (
            <div className="casino-lobby__icon">
              <img
                alt={blockTitleKey}
                height={24}
                onError={handleError}
                role="presentation"
                src={
                  imageUrl
                    ? `${encodeURI(imageUrlCDN(imageUrl, CDN_TYPE_CMS))}`
                    : `${BASE_PATH || ''}/static/assets/images/tournaments.png`
                }
                width={24}
              />
            </div>
          )}
          {blockTitleKey && (
            <div className="casino-lobby__title">
              <Label message={blockTitleKey} />
            </div>
          )}
        </div>
      </div>

      <div className="casino-lobby__content">
        <div className="lobby-tournaments__slider">
          <Slider
            autoplay={{
              delay: 2000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            dir={dir}
            direction="horizontal"
            loop={false}
            spaceBetween={8}
            speed={1000}
          >
            {tournaments.map(tournament => (
              <Slider.Slide key={tournament.instance_id}>
                <TournamentsCard
                  pathname="/tournaments"
                  push={push}
                  timeLeftForTournaments={timeLeftForTournaments}
                  tournament={tournament}
                />
              </Slider.Slide>
            ))}
          </Slider>
        </div>
      </div>
    </div>
  );
};

LobbyTournamentsBlock.propTypes = {
  blockTitleKey: PropTypes.string,
  imageUrl: PropTypes.string,
};

export default LobbyTournamentsBlock;
